import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, ChevronsRight, SearchX } from 'lucide-react'
import { EmptyState } from '../components/radix'
import { AdminOrderStatusPill } from '../components/orders/AdminOrderStatusPill'
import { Badge, KindChip, NonSupplyScannerChip } from '../components/orders/SupplierChangeParts'
import { SupplierChangesRail } from '../components/orders/SupplierChangesRail'
import type { RailCurrentOrder } from '../components/orders/SupplierChangesRail'
import { useOrderSupplierChanges } from '../auth/hooks/useSupplierChanges'
import { useMediaQuery } from '../hooks/useMediaQuery'
import { formatCurrency, formatPoundsDifference, formatUnitPrice } from '../utils/currency'
import { formatDateTime, formatShortWeekdayDate } from '../utils/datetime'
import { EMPTY_VALUE } from '../constants/display'
import type { OrderSupplierChanges, SupplierChangeLine, SupplierChangeTimelineEntry } from '../auth/types'
import {
  BadgeTone,
  CHANGE_RISK_BADGE,
  FOCUS_RING,
  QUIET_BUTTON,
  RAIL_BESIDE_PAGE_QUERY,
  SUPPLIER_CHANGES_RAIL_OPEN_KEY,
  SUPPLIER_CHANGES_TAB_PARAM,
  SupplierChangeKind,
  SupplierTimelineChange,
} from '../constants/supplierChanges'

// An order's supplier changes (PO-009, read-only Phase 1), laid out as the
// mock's Option J order page: who and where, the supplier changes card (each
// change as ordered against reported, or every line item), then the
// supplier's changes over time. Opened from "Supplier changes to review";
// the full order stays on its own page. No accept, reject or edit (AC6).

type View = 'changes' | 'all'

const NOT_FOUND_STATUS = 404
const FORBIDDEN_STATUS = 403

const pounds = (amount: string | null) => (amount === null ? '' : formatCurrency(parseFloat(amount), 2))
const unitPrice = (amount: string | null) => (amount === null ? '' : formatUnitPrice(parseFloat(amount)))

function hasKind(line: SupplierChangeLine, kind: SupplierChangeKind): boolean {
  return line.changes.some((change) => change.kind === kind)
}

/** The rail's shown / hidden choice, kept between visits (localStorage). */
function useRailOpen(): [boolean, (isOpen: boolean) => void] {
  const [isOpen, setIsOpen] = useState(() => {
    try {
      return window.localStorage.getItem(SUPPLIER_CHANGES_RAIL_OPEN_KEY) !== 'false'
    } catch {
      return true
    }
  })
  const save = (next: boolean) => {
    setIsOpen(next)
    try {
      window.localStorage.setItem(SUPPLIER_CHANGES_RAIL_OPEN_KEY, String(next))
    } catch {
      // Storage blocked: the choice lasts for this visit.
    }
  }
  return [isOpen, save]
}

/** The open order as a rail row, for when its row isn't loaded in the rail. */
function railCurrentOrder(data: OrderSupplierChanges): RailCurrentOrder {
  const { order, totals, lines } = data
  return {
    id: order.id,
    supplierId: order.supplier_id,
    row: {
      id: order.id,
      order_number: order.order_number,
      status: order.status,
      created_at: order.created_at,
      total_amount: totals.ordered_total,
      tenant_name: order.tenant_name,
      engineer_name: order.engineer_name,
      supplier_id: order.supplier_id,
      supplier_name: order.supplier_name,
      branch_name: order.branch_name,
      line_review_status: order.line_review_status,
      oldest_change_at: null,
      changed_line_count: lines.filter((line) => line.changes.length > 0).length,
      supplier_change: totals.difference,
      change_kinds: [],
      non_catalogue_count: 0,
    },
  }
}

export function SupplierChangeOrder() {
  const { tenantSlug, orderId } = useParams()
  const { data, isLoading, error } = useOrderSupplierChanges(orderId)
  const [isRailOpen, setRailOpen] = useRailOpen()
  // The rail beside the page is only mounted when it can be seen, so a
  // narrow screen doesn't load it behind the scenes.
  const isWideScreen = useMediaQuery(RAIL_BESIDE_PAGE_QUERY)
  // Below 1280px wide the rail opens over the page instead of beside it.
  const [isDrawerOpen, setDrawerOpen] = useState(false)

  useEffect(() => {
    if (!isDrawerOpen) return
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setDrawerOpen(false)
    }
    window.addEventListener('keydown', closeOnEscape)
    return () => window.removeEventListener('keydown', closeOnEscape)
  }, [isDrawerOpen])
  const backHref = `/${tenantSlug}/orders?tab=${SUPPLIER_CHANGES_TAB_PARAM}`
  const status = (error as { status?: number } | null)?.status
  const isMissing = status === NOT_FOUND_STATUS || status === FORBIDDEN_STATUS
  // The header and the rail stay in place while another order loads; only
  // the order's own section shows the loading state.
  const order = data?.order
  const railCurrent: RailCurrentOrder = data ? railCurrentOrder(data) : { id: orderId ?? '', supplierId: null, row: null }

  let content: ReactNode
  if (isLoading) {
    content = <OrderSectionSkeleton />
  } else if (error || !data) {
    content = (
      <div className="rounded-lg border border-gray-200 bg-white">
        <EmptyState
          icon={<SearchX className="h-10 w-10 text-gray-400" aria-hidden="true" />}
          title={isMissing ? "We can't find supplier changes for this order" : 'Failed to load supplier changes'}
          description={isMissing ? 'The supplier changed nothing on it, or it isn’t one you can see.' : 'Please try again later.'}
          action={<Link to={backHref} className={QUIET_BUTTON}>Back to supplier changes</Link>}
        />
      </div>
    )
  } else {
    content = (
      <>
        <WhoAndWhere data={data} />
        <ChangesCard key={data.order.id} data={data} />
        <OrderInformation data={data} />
        <Timeline data={data} />
      </>
    )
  }

  return (
    <div className="space-y-4">
      <PageHeader
        backHref={backHref}
        title={order ? `#${order.order_number ?? ''}` : ''}
        marks={order && <AdminOrderStatusPill status={order.status} size="md" />}
        right={
          <>
            {!isRailOpen && (
              <button type="button" onClick={() => setRailOpen(true)} className={`${QUIET_BUTTON} hidden h-9 xl:inline-flex`}>
                <ChevronsRight className="h-4 w-4" aria-hidden="true" />
                Supplier changes to review
              </button>
            )}
            <button type="button" onClick={() => setDrawerOpen(true)} aria-expanded={isDrawerOpen} className={`${QUIET_BUTTON} h-9 xl:hidden`}>
              <ChevronsRight className="h-4 w-4" aria-hidden="true" />
              Supplier changes to review
            </button>
          </>
        }
      />
      <div className="flex items-start gap-5">
        {isRailOpen && isWideScreen && (
          // The rail's column is as tall as the order beside it and the rail
          // only sticks within it, so the rail never makes the page scroll
          // further than the order's own content.
          <div className="hidden w-72 flex-shrink-0 self-stretch xl:block">
            <div className="sticky top-16">
              <SupplierChangesRail current={railCurrent} onHide={() => setRailOpen(false)} />
            </div>
          </div>
        )}
        {isDrawerOpen && (
          <div className="fixed inset-0 z-30 bg-gray-900/20 xl:hidden" onClick={() => setDrawerOpen(false)}>
            <div className="absolute left-0 top-0 h-full bg-gray-50 p-4 shadow-xl" onClick={(event) => event.stopPropagation()}>
              {/* Picking an order closes the drawer, so it doesn't cover the order it opens. */}
              <SupplierChangesRail current={railCurrent} onHide={() => setDrawerOpen(false)} onPickOrder={() => setDrawerOpen(false)} />
            </div>
          </div>
        )}
        <div className="min-w-0 flex-1 space-y-4">{content}</div>
      </div>
    </div>
  )
}

function PageHeader({ backHref, title, marks, right }: { backHref: string; title: string; marks?: ReactNode; right?: ReactNode }) {
  return (
    <div className="sticky -top-4 z-10 -mx-4 -mt-4 mb-2 border-b border-gray-200 bg-white sm:-top-6 sm:-mx-6 sm:-mt-6 lg:-top-8 lg:-mx-8 lg:-mt-8">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3">
        <Link to={backHref} aria-label="Back to supplier changes to review" className={`-ml-1.5 flex-shrink-0 rounded-lg p-1.5 text-gray-600 transition-colors hover:bg-gray-100 ${FOCUS_RING}`}>
          <ArrowLeft className="h-5 w-5" aria-hidden="true" />
        </Link>
        <h1 className="truncate text-sm font-bold text-gray-900 sm:text-base">{title}</h1>
        {marks}
        {right && <div className="ml-auto flex items-center gap-2">{right}</div>}
      </div>
    </div>
  )
}

/** "Engineer, Tenant · Wolseley Derry · Placed 14 Oct 2026, 13:21". */
function WhoAndWhere({ data }: { data: OrderSupplierChanges }) {
  const { order } = data
  const parts: ReactNode[] = [
    <span key="who"><b className="font-semibold text-gray-900">{order.engineer_name}</b>{order.tenant_name ? `, ${order.tenant_name}` : ''}</span>,
    <span key="where">{[order.supplier_name, order.branch_name].filter(Boolean).join(' ')}</span>,
    <span key="placed">Placed {formatDateTime(order.created_at)}</span>,
  ]
  const fulfillment = fulfillmentWords(data)
  if (fulfillment) parts.push(<span key="when">{fulfillment}</span>)
  return (
    <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-gray-700">
      {parts.map((part, index) => (
        <span key={index} className="inline-flex items-center gap-2">
          {index > 0 && <span aria-hidden="true" className="text-gray-400">·</span>}
          {part}
        </span>
      ))}
    </p>
  )
}

/** "Collection Fri 16 Oct" / "Delivery Fri 16 Oct", or null when there's no date. */
function fulfillmentWords(data: OrderSupplierChanges): string | null {
  const { fulfillment_type: type, fulfillment_date: day } = data.order_info
  if (!day) return null
  // Read at midday UTC so no timezone can move the day.
  const label = type === 'delivery' ? 'Delivery' : 'Collection'
  return `${label} ${formatShortWeekdayDate(new Date(`${day}T12:00:00Z`))}`
}

/** Grey blocks shaped like the order's section while it loads. */
function OrderSectionSkeleton() {
  return (
    <div className="animate-pulse space-y-4" aria-hidden="true">
      <div className="h-4 w-2/3 rounded bg-gray-200" />
      <div className="space-y-3 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <div className="h-5 w-40 rounded bg-gray-200" />
        <div className="h-4 w-72 rounded bg-gray-100" />
        <div className="h-20 rounded bg-gray-100" />
        <div className="h-20 rounded bg-gray-100" />
      </div>
      <div className="h-40 rounded-lg border border-gray-200 bg-white shadow-sm" />
    </div>
  )
}

function InfoRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 border-t border-gray-50 py-2 first:border-t-0 first:pt-0 last:pb-0">
      <span className="text-gray-500">{label}</span>
      <span className="text-right font-medium text-gray-900">{children}</span>
    </div>
  )
}

/** Option J's Order information card. */
function OrderInformation({ data }: { data: OrderSupplierChanges }) {
  const { order, order_info: info } = data
  const reason = [info.reason, info.secondary_reason].filter(Boolean).join(' · ')
  const fulfillmentDay = info.fulfillment_date ? formatShortWeekdayDate(new Date(`${info.fulfillment_date}T12:00:00Z`)) : EMPTY_VALUE
  return (
    <section aria-labelledby="order-information-title" className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="border-b border-gray-100 px-3.5 py-2">
        <h2 id="order-information-title" className="text-sm font-medium uppercase tracking-wider text-gray-500">Order information</h2>
      </div>
      <div className="grid grid-cols-1 gap-x-8 gap-y-2 px-4 py-3 text-sm md:grid-cols-2">
        <div>
          <InfoRow label="Tenant">{order.tenant_name ?? EMPTY_VALUE}</InfoRow>
          <InfoRow label="Engineer">{order.engineer_name}</InfoRow>
          <InfoRow label="Submitted">{info.submitted_at ? formatDateTime(info.submitted_at) : EMPTY_VALUE}</InfoRow>
          <InfoRow label="Job number"><span className="font-mono text-xs">{info.job_number || EMPTY_VALUE}</span></InfoRow>
          <InfoRow label="Reason">{reason || EMPTY_VALUE}</InfoRow>
        </div>
        <div>
          <InfoRow label="Supplier">
            {[order.supplier_name, order.branch_name].filter(Boolean).join(' ')}
            {info.branch_address && <span className="block text-xs font-normal text-gray-500">{info.branch_address}</span>}
            {info.branch_phone && <span className="block text-xs font-normal text-gray-500">{info.branch_phone}</span>}
          </InfoRow>
          <InfoRow label={info.fulfillment_type === 'delivery' ? 'Delivery' : 'Collection'}>
            {fulfillmentDay}
            {info.delivery_address && <span className="block text-xs font-normal text-gray-500">To {info.delivery_address}</span>}
          </InfoRow>
          <InfoRow label="Supplier order number">{info.supplier_order_id ?? <span className="font-normal text-gray-500">Not received yet</span>}</InfoRow>
          <InfoRow label="Supplier status">{info.supplier_status ?? <span className="font-normal text-gray-500">Not reported yet</span>}</InfoRow>
          {info.backorder_status && <InfoRow label="Backorder status">{info.backorder_status}</InfoRow>}
        </div>
      </div>
    </section>
  )
}

function Segmented({ value, onChange, options }: { value: View; onChange: (view: View) => void; options: { value: View; label: string }[] }) {
  return (
    <div role="radiogroup" aria-label="Show" className="ml-auto inline-grid auto-cols-fr grid-flow-col gap-1 rounded-xl bg-gray-100 p-1">
      {options.map((option) => {
        const isPicked = option.value === value
        return (
          <button key={option.value} type="button" role="radio" aria-checked={isPicked} onClick={() => onChange(option.value)} className={`inline-flex h-9 items-center justify-center whitespace-nowrap rounded-lg px-3 text-sm font-semibold transition-colors ${FOCUS_RING} ${isPicked ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}>
            {option.label}
          </button>
        )
      })}
    </div>
  )
}

/** The supplier changes: the totals (AC4), then each change or every line item. */
function ChangesCard({ data }: { data: OrderSupplierChanges }) {
  const [view, setView] = useState<View>('changes')
  const { order, report, totals, lines } = data
  const supplier = order.supplier_name ?? 'The supplier'
  const changed = lines.filter((line) => line.changes.length > 0)
  const orderedCount = lines.filter((line) => line.source === 'ordered').length
  const difference = parseFloat(totals.difference)
  return (
    <section aria-labelledby="changes-title" className="overflow-clip rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="space-y-2 border-b border-gray-100 px-4 py-3">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <h2 id="changes-title" className="text-lg font-semibold text-gray-900">Supplier changes</h2>
          <Segmented value={view} onChange={setView} options={[{ value: 'changes', label: `Changes only (${changed.length})` }, { value: 'all', label: `All line items (${lines.length})` }]} />
        </div>
        <p className="text-sm tabular-nums text-gray-700">
          {supplier} total <b className="text-lg font-bold text-gray-900">{pounds(totals.supplier_total)}</b> of {pounds(totals.ordered_total)} ordered
          <span className="text-gray-500"> · </span>
          <b className="font-semibold text-gray-900">{difference === 0 ? 'No difference' : formatPoundsDifference(difference)}</b>
        </p>
        <p className="text-xs text-gray-500">
          {report.lines_as_ordered} of {report.lines_sent} line items as ordered
          {report.reported_at && <> · From {supplier}&apos;s report, {formatDateTime(report.reported_at)}</>}
        </p>
      </div>
      {view === 'changes' ? (
        <div className="divide-y divide-gray-200">
          {changed.map((line) => <ChangeArticle key={line.id} line={line} supplier={supplier} orderedCount={orderedCount} />)}
        </div>
      ) : (
        <LineItemsTable lines={lines} supplier={supplier} />
      )}
    </section>
  )
}

/** "10p more each, on 2", "1 fewer than ordered", "None will be supplied", "Not ordered: Wolseley added it". */
function changeWords(line: SupplierChangeLine, supplier: string): string {
  if (line.source === 'supplier_added') return `Not ordered: ${supplier} added it`
  if (hasKind(line, SupplierChangeKind.Removed)) return `Not in ${supplier}'s report`
  const ordered = line.ordered
  const reported = line.reported
  if (!ordered || !reported || reported.quantity === null) return ''
  if (reported.quantity === 0) return 'None will be supplied'
  const parts: string[] = []
  if (line.changed_values.includes('quantity')) {
    const gap = reported.quantity - (ordered.quantity ?? 0)
    parts.push(gap < 0 ? `${-gap} fewer than ordered` : `${gap} more than ordered`)
  }
  if (line.changed_values.includes('unit_price') && reported.unit_price !== null && ordered.unit_price !== null) {
    const gap = parseFloat(reported.unit_price) - parseFloat(ordered.unit_price)
    const each = `${formatUnitPrice(Math.abs(gap))} ${gap > 0 ? 'more' : 'less'} each`
    parts.push(line.changed_values.includes('quantity') ? each : `${each}, on ${ordered.quantity}`)
  }
  return parts.join(', ')
}

/** One change: what changed with its risk (AC2), then ordered against reported. */
function ChangeArticle({ line, supplier, orderedCount }: { line: SupplierChangeLine; supplier: string; orderedCount: number }) {
  const place = line.line_number === null ? 'New line item' : `Line ${line.line_number} of ${orderedCount} ordered`
  return (
    <article aria-label={`Supplier change on ${line.product_name ?? 'line item'}`}>
      <div className="flex items-start gap-4 px-4 pb-2.5 pt-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            {line.changes.map((change) => (
              <span key={change.kind} className="inline-flex items-center gap-1">
                <KindChip kind={change.kind} />
                <Badge tone={CHANGE_RISK_BADGE[change.risk].tone}>{CHANGE_RISK_BADGE[change.risk].label}</Badge>
              </span>
            ))}
            <h3 className="text-base font-semibold text-gray-900">{line.product_name}</h3>
          </div>
          <p className="mt-0.5 text-sm text-gray-700">
            {changeWords(line, supplier)}
            <span className="text-xs text-gray-500"> · {place}</span>
          </p>
        </div>
        <p className="w-28 flex-shrink-0 text-right text-base font-bold tabular-nums text-gray-900">
          {line.supplier_change !== null && formatPoundsDifference(parseFloat(line.supplier_change))}
        </p>
      </div>
      <ChangeRows line={line} supplier={supplier} />
    </article>
  )
}

/** What the supplier changed, marked on the reported side only. */
function Mark({ isOn, children }: { isOn: boolean; children: ReactNode }) {
  if (!isOn) return <>{children}</>
  return <span className="rounded bg-amber-100 px-1 font-semibold text-amber-900">{children}</span>
}

const DIFF_ROW = 'grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-x-3 px-4 py-1.5 text-sm md:grid-cols-[minmax(5.5rem,150px)_minmax(0,1fr)_minmax(5.5rem,120px)_minmax(4.5rem,88px)]'

function DiffRow({ isReported, label, product, values, total }: { isReported: boolean; label: string; product: ReactNode; values: ReactNode; total: ReactNode }) {
  return (
    <div className={`${DIFF_ROW} ${isReported ? 'bg-white' : 'bg-gray-50'}`}>
      <span className={`col-span-3 text-xs font-semibold md:col-span-1 ${isReported ? 'text-gray-900' : 'text-gray-600'}`}>{label}</span>
      <span className="min-w-0 break-words text-gray-900">{product}</span>
      <span className="whitespace-nowrap tabular-nums text-gray-900">{values}</span>
      <span className="whitespace-nowrap text-right font-semibold tabular-nums text-gray-900">{total}</span>
    </div>
  )
}

/** "Ordered" against "Wolseley reported", with only what changed marked (AC1). */
function ChangeRows({ line, supplier }: { line: SupplierChangeLine; supplier: string }) {
  const { ordered, reported } = line
  const isQuantityChanged = line.changed_values.includes('quantity')
  const isPriceChanged = line.changed_values.includes('unit_price')
  const isAdded = line.source === 'supplier_added'
  return (
    <div role="group" aria-label={`Ordered against what ${supplier} reported`} className="divide-y divide-gray-100 border-y border-gray-100">
      {ordered ? (
        <DiffRow isReported={false} label="Ordered" product={<span className="font-mono text-xs text-gray-700">{ordered.product_code}</span>} values={`${ordered.quantity} × ${unitPrice(ordered.unit_price)}`} total={pounds(ordered.line_total)} />
      ) : (
        <DiffRow isReported={false} label="Ordered" product={<span className="text-gray-600">Not ordered</span>} values="" total="" />
      )}
      <DiffRow
        isReported
        label={`${supplier} reported`}
        product={
          reported ? (
            <span className="inline-flex flex-wrap items-center gap-1.5">
              <Mark isOn={isAdded}>
                <span className="font-mono text-xs">{reported.product_code}</span>
                {isAdded && ` ${line.product_name ?? ''}`}
              </Mark>
              {reported.quantity === 0 && <Badge>None supplied</Badge>}
              {line.is_non_catalogue && <NonSupplyScannerChip />}
            </span>
          ) : (
            <span className="text-gray-600">Not in {supplier}&apos;s report</span>
          )
        }
        values={reported ? <><Mark isOn={isQuantityChanged || isAdded}>{reported.quantity}</Mark> × <Mark isOn={isPriceChanged || isAdded}>{unitPrice(reported.unit_price)}</Mark></> : ''}
        total={reported ? pounds(reported.line_total) : ''}
      />
    </div>
  )
}

/** What the supplier reported for a line in the all-line-items table. */
function reportedText(line: SupplierChangeLine, supplier: string): { text: string; isQuiet: boolean } {
  if (!line.is_sent) return { text: `Not sent to ${supplier}`, isQuiet: true }
  if (!line.reported) return { text: `Not in ${supplier}'s report`, isQuiet: false }
  if (line.changes.length === 0) return { text: 'As ordered', isQuiet: true }
  if (line.reported.quantity === 0) return { text: '0, cancelled', isQuiet: false }
  return { text: `${line.reported.quantity} × ${unitPrice(line.reported.unit_price)}`, isQuiet: false }
}

const CELL = 'px-3 py-2'

/** Every line item: ordered, what the supplier reported and what the line now comes to. */
function LineItemsTable({ lines, supplier }: { lines: SupplierChangeLine[]; supplier: string }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" aria-label="All line items">
        <thead className="border-b border-gray-200 bg-gray-50">
          <tr className="text-left text-xs font-semibold text-gray-600">
            <th scope="col" className="w-14 px-4 py-2">Line</th>
            <th scope="col" className={CELL}>Product</th>
            <th scope="col" className={CELL}>Ordered</th>
            <th scope="col" className={CELL}>{supplier} reported</th>
            <th scope="col" className={`${CELL} pr-4 text-right`}>Line total</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {lines.map((line) => {
            const reported = reportedText(line, supplier)
            const hasChanges = line.changes.length > 0
            return (
              <tr key={line.id} className={`align-top ${hasChanges ? 'bg-amber-50/40' : ''} ${line.is_sent ? '' : 'text-gray-500'}`}>
                <td className="px-4 py-2 text-gray-500">{line.line_number ?? 'New'}</td>
                <td className={`${CELL} min-w-40`}>
                  <span className="flex flex-wrap items-center gap-1.5">
                    <span className={line.is_sent ? 'text-gray-900' : 'text-gray-500'}>{line.product_name}</span>
                    {!line.is_sent && <Badge>Not sent to supplier</Badge>}
                    {line.risk && <Badge tone={CHANGE_RISK_BADGE[line.risk].tone}>{CHANGE_RISK_BADGE[line.risk].label}</Badge>}
                    {line.is_non_catalogue && <Badge tone={BadgeTone.Purple}>Non-Supply Scanner product</Badge>}
                  </span>
                  <span className="font-mono text-xs text-gray-500">{line.ordered?.product_code ?? line.reported?.product_code}</span>
                </td>
                <td className={`${CELL} whitespace-nowrap tabular-nums text-gray-700`}>
                  {line.ordered ? `${line.ordered.quantity} × ${unitPrice(line.ordered.unit_price)}` : <span className="text-gray-500">Not ordered</span>}
                  {line.ordered && !line.is_sent && <span className="block text-xs text-gray-500">{line.ordered.requested_quantity} requested</span>}
                </td>
                <td className={`${CELL} tabular-nums ${reported.isQuiet ? 'text-gray-500' : 'whitespace-nowrap text-gray-900'}`}>{reported.text}</td>
                <td className={`${CELL} whitespace-nowrap pr-4 text-right font-semibold tabular-nums text-gray-900`}>{pounds(line.supplier_line_total)}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

/** "Wolseley reduced quantity from 5 to 4", as the ticket's example (AC5). */
function timelineWords(entry: SupplierChangeTimelineEntry, supplier: string): string {
  const before = entry.before === null ? 0 : parseFloat(entry.before)
  const after = entry.after === null ? 0 : parseFloat(entry.after)
  switch (entry.change) {
    case SupplierTimelineChange.Quantity:
      return `${supplier} ${after < before ? 'reduced' : 'increased'} quantity from ${before} to ${after}`
    case SupplierTimelineChange.UnitPrice:
      return `${supplier} ${after < before ? 'lowered' : 'raised'} the price from ${formatUnitPrice(before)} to ${formatUnitPrice(after)}`
    case SupplierTimelineChange.Added:
      return `${supplier} added ${after} × ${unitPrice(entry.unit_price)}`
    case SupplierTimelineChange.Removed:
      return `${supplier} removed it from the order`
    case SupplierTimelineChange.NoLongerListed:
      return `${supplier} no longer lists it`
    default:
      return ''
  }
}

/** The activity log of supplier changes: what changed, before / after and when (AC5). */
function Timeline({ data }: { data: OrderSupplierChanges }) {
  const supplier = data.order.supplier_name ?? 'The supplier'
  const entries = data.timeline
  return (
    <section aria-labelledby="supplier-history-title" className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-b border-gray-100 px-3.5 py-2">
        <h2 id="supplier-history-title" className="text-sm font-medium uppercase tracking-wider text-gray-500">Supplier change history</h2>
        <div className="text-sm text-gray-500">{entries.length} entr{entries.length === 1 ? 'y' : 'ies'}</div>
      </div>
      {entries.length === 0 ? (
        <p className="px-4 py-3 text-sm text-gray-500">No supplier changes recorded.</p>
      ) : (
        <ol className="divide-y divide-gray-100">
          {entries.map((entry, index) => (
            <li key={`${entry.at}-${index}`} className="grid grid-cols-1 gap-x-4 px-4 py-2 text-sm sm:grid-cols-[150px_minmax(0,1fr)]">
              <span className="tabular-nums text-gray-500">{formatDateTime(entry.at)}</span>
              <span className="min-w-0">
                <span className="font-medium text-gray-900">{timelineWords(entry, supplier)}</span>
                <span className="text-gray-700"> · {entry.product_name}</span>
              </span>
            </li>
          ))}
        </ol>
      )}
    </section>
  )
}
